import { PlatformType } from '@microsoft/office-js-helpers'
import moment from 'moment'
import momentTz from 'moment-timezone'
import { refreshAuthData, ServerSession } from '../session'
import { LoftMessages, getLoftMessages, notificationTypes } from './constants'
import { onPromiseTimeout } from '../../office/utils/promise'
import { getMessageIds } from '../../office/item'
import { melodyRequest, publicRequest } from '../../gateway/request'
import { LoftAsyncProps, LoftRecord } from './record'
import {
  LoftServices,
  LoftFromOutlookResponse,
  FindByEmailResponse,
  Recipient,
} from '../../types/loft'
import { Me } from '../../types/me'
import { insertOutlookCustomData } from '../../actions/insertToOutlook'
import { refreshLocaleData } from '../../platform/localization'
import { getAccountId, getCadenceId, getStepId, resolveRecipientName } from '../loftCalendarEvents'
import { isDesktopWindows } from '../../office/client'
import { getPersonByEmail } from '../people'
import { OfficeWrapper } from '../../office/office'
import { addBreadcrumb, addEventProcessor, setContext, setUser } from '../../sentry-config'

export const BACKEND_AZURE = 'backend-azure'
export const FRONTEND_EWS = 'frontend-ews'
export const CALENDAR_LOFTING = 'CALENDAR_LOFTING'

const emptyRecipients = {
  to: [],
  cc: [],
  bcc: [],
}

const filterRecipients = (recipients: Recipient[], mainRecipient: string | null) =>
  recipients.reduce((acc: string[], { emailAddress }: Recipient) => {
    if (emailAddress !== mainRecipient) {
      acc.push(emailAddress)
    }
    return acc
  }, [])

const noop = () => undefined
export const logAndResolve = (value: null | false | LoftRecord = null) => (
  e: Record<string, unknown> | null = null
) => {
  console.error('resolveWithLog', e, value)
  return Promise.resolve(value)
}
export const awaitRecipient = async (state: LoftRecord) => {
  const user = await state.getAsync('me').catch(noop)

  const response: Promise<FindByEmailResponse[] | FindByEmailResponse> = await getPersonByEmail(
    state.emailAddress || '',
    {
      features: user?.data._private_fields.features || {},
      user_guid: user?.data.guid || '',
    }
  )

  return state.setAsync('recipient', Array.isArray(response) ? response[0] || null : response)
}

export const updateParentEmail = async (state: LoftRecord, { office }: LoftServices) => {
  const ewsMessage = await state.getAsync('ewsMessage')
  const me = await state.getAsync('me').catch(noop)

  if (ewsMessage) {
    if (me?.data._private_fields.features.nested_app_auth_enabled) {
      return
    }
    const { inReplyTo } = ewsMessage
    return state.setAsync('updateParentEmail', await office.exchange.updateParentEmail(inReplyTo))
  }
  return state.setAsync('updateParentEmail', Promise.resolve(null))
}

export const sendEmail = async (
  state: LoftRecord,
  { gateway, office }: LoftServices
): Promise<void> => {
  try {
    const ewsMessage = await state.getAsync('ewsMessage')
    const me = await state.getAsync('me').catch(noop)

    if (!state.email) {
      // noinspection ExceptionCaughtLocallyJS
      throw new Error('Tracking Body is not defined')
    }

    if (!ewsMessage) {
      throw new Error('missing ewsMessage')
    }

    const recipients = await office.item?.getRecipients()

    if (me?.data._private_fields.features.nested_app_auth_enabled) {
      await office.graphApi.updateAndSendItem(
        ewsMessage.id,
        state.email.tracking_body,
        state.email.subject,
        recipients || emptyRecipients
      )
      return
    }
    await office.exchange.updateAndSendItem(
      ewsMessage.id,
      ewsMessage.changeKey,
      state.email.tracking_body,
      state.email.subject,
      recipients || emptyRecipients
    )
  } catch (e) {
    if (e !== 'ErrorIrresolvableConflict') {
      console.error(e)
      throw e
    }

    await new Promise(resolve => setTimeout(resolve, 5000))
    state = await ewsGetMessage(state, { gateway, office })

    return sendEmail(state, { gateway, office })
  }
}

export const setRestMessage = async (state: LoftRecord, { office }: LoftServices) => {
  const teamEmailSettings = await state.getAsync('teamEmailSettings')
  const me = await state.getAsync('me').catch(noop)

  if (teamEmailSettings?.outlook_disallow_rest) {
    office.item.restMessage = null
  } else {
    // @ts-expect-error restMessage is not ts yet
    office.item.restMessage = await office.item.rest
      .getCurrentMessage(
        state.restItemId,
        me?.data._private_fields.features.nested_app_auth_enabled
      )
      .catch(logAndResolve(false))
  }
}

export const setMessageIds = async (state: LoftRecord) => {
  const { restItemId, ewsItemId } = await getMessageIds()

  return state.withMutations(ctx => ctx.set('restItemId', restItemId).set('ewsItemId', ewsItemId))
}

export const safeSetMessageIds = (state: LoftRecord) =>
  onPromiseTimeout(setMessageIds(state).catch(logAndResolve(state)), logAndResolve(state), 5000)

export const ewsGetMessage = async (state: LoftRecord, { office }: LoftServices) => {
  state = await safeSetMessageIds(state)
  if (!state.ewsItemId) {
    throw new Error('ewsItemId is not defined')
  }
  const me = await state.getAsync('me').catch(noop)

  return state.setAsync(
    'ewsMessage',
    me?.data._private_fields.features.nested_app_auth_enabled
      ? await office.graphApi.waitSyncAndGetItemById(state.ewsItemId)
      : await office.exchange.waitSyncAndGetItemById(state.ewsItemId)
  )
}

export const setSession = async (state: LoftRecord) => {
  const session: ServerSession = await refreshAuthData()

  return state.set('session', session)
}

export const setRecipientEmail = async (state: LoftRecord) => {
  const to = await state.getAsync('to')
  if (!to || to.length === 0) {
    return state
  }
  const [{ emailAddress }] = to
  return state.set('emailAddress', emailAddress)
}

export const addDraftMailingIdToState = async (state: LoftRecord, { office }: LoftServices) => {
  try {
    const customProperties = await office.item?.getCustomProperties()
    const draftMailingId = await customProperties.get('draftMailingId')

    return state.set('draftMailingId', draftMailingId)
  } catch (e) {
    addBreadcrumb({
      message: 'addDraftMailingIdToState',
      category: 'office-js',
      level: 'error',
      data: e,
    })
  }
}

export const melodyLoft = async (state: LoftRecord, { gateway, office }: LoftServices) => {
  const recipients = await office.item?.getRecipients()

  const response: LoftFromOutlookResponse = await gateway.mailings.loftFromOutlook({
    to: state.emailAddress || '',
    additionalTo: filterRecipients(recipients?.to || [], state.emailAddress),
    subject: (await state.getAsync('subject')) || '',
    body: (await state.getAsync('body')) || '',
    attachments: (await state.getAsync('attachments')) || [],
    templateId: (await state.getAsync('templateId')) || null,
    crmReferences: (await state.getAsync('crmReferences')) || {},
    draftMailingId: state.draftMailingId,
    cc: recipients?.cc?.map(({ emailAddress }: Recipient) => emailAddress) || [],
  })

  const [email] = response.emails

  return state.withMutations(ctx => ctx.set('mailingId', parseInt(response.id)).set('email', email))
}

export const getEventData = async (state: LoftRecord, { gateway }: LoftServices) => {
  const itemId = (await state.getAsync('itemId').catch(noop)) || ''

  try {
    const response = await gateway.calendarLofting.getLoftedEvent(itemId)

    return state.setAsync('eventData', response)
  } catch (e) {
    return null
  }
}

export const calendarLoft = async (state: LoftRecord, { gateway }: LoftServices) => {
  const calendarSettings = await state.getAsync('calendarSettings').catch(noop)
  const attendees = (await state.getAsync('attendees').catch(noop)) || ([] as Recipient[])
  const organizer = await state.getAsync('organizer').catch(noop)
  const mainGuest = await state.getAsync('mainGuest').catch(noop)
  const start = await state.getAsync('startTime').catch(noop)
  const end = await state.getAsync('endTime').catch(noop)
  const itemId = await state.getAsync('itemId').catch(noop)
  const eventData = await state.getAsync('eventData').catch(noop)
  const meetingTypeId = await state.getAsync('meetingTypeId').catch(noop)
  const me = await state.getAsync('me').catch(noop)

  const startTime = moment(start)
    .utc()
    .format()
  const endTime = moment(end)
    .utc()
    .format()

  const isCreation = !eventData || !eventData.event_id

  await gateway.calendarLofting.loft({
    azure_forward_event:
      isCreation &&
      isDesktopWindows() &&
      !me?.data._private_fields.features.outlook_windows_classic_onsend_lofting,
    isCreation,
    event_id: itemId,
    calendar_id: calendarSettings?.data.primary_calendar_id || '',
    calendar_type: calendarSettings?.data.calendar_type || '',
    meeting_type_id: meetingTypeId === 'default_personal_meeting' ? null : meetingTypeId,
    title: (await state.getAsync('subject')) || '',
    recurrence: [],
    location: (await state.getAsync('location')) || null,
    reminders: { use_default: true },
    description: (await state.getAsync('body')) || '',
    attendees: attendees.map(({ organizer: isOrganizer, displayName, emailAddress, optional }) => ({
      organizer: isOrganizer || organizer?.emailAddress === emailAddress || false,
      name: displayName || '',
      email: emailAddress || '',
      optional: !!optional,
      resource: false,
      status: isOrganizer || organizer?.emailAddress === emailAddress ? 'accepted' : 'needsAction',
    })),
    rooms: [], // rooms will be added in future
    calendar_owner: organizer?.emailAddress || '',
    mark_busy: true,
    visibility: 'default',
    all_day: Number.isInteger(moment(end).diff(moment(start), 'days', true)),
    conference_data: null,
    start: {
      timezone: momentTz(start).format('Z'),
      date_time: startTime,
    },
    end: {
      timezone: momentTz(end).format('Z'),
      date_time: endTime,
    },
    should_sync_event: true,
    crm_custom_fields: (await state.getAsync('crmCustomFields')) || {},
    recipient_email: mainGuest?.emailAddress,
    recipient_name: resolveRecipientName(mainGuest),
    recipient_phone: mainGuest && 'phone' in mainGuest ? mainGuest.phone : null,
    recipient_company:
      mainGuest && 'person_company_name' in mainGuest ? mainGuest.person_company_name : null,
    person_id: mainGuest && 'id' in mainGuest ? mainGuest.id : null,
    account_id: getAccountId(mainGuest),
    cadence_id: getCadenceId(mainGuest),
    step_id: getStepId(mainGuest),
    attachments: (await state.getAsync('attachments')) || [],
    crm_references: (await state.getAsync('crmReferences')) || {},
  })
}

export const addDraftMailingIdToCustomProperties = async (state: LoftRecord) => {
  try {
    if (!state.draftMailingId || state.draftMailingId !== state.mailingId) {
      await insertOutlookCustomData('draftMailingId', state.mailingId)
    }
  } catch (e) {
    addBreadcrumb({
      message: 'addDraftMailingIdToCustomProperties',
      category: 'office-js',
      level: 'error',
      data: e,
    })
  }
}

async function isReplyMessage(officeWrapper: OfficeWrapper): Promise<boolean> {
  const item = officeWrapper.item

  // Check if the item has a conversation ID (which indicates it's part of a thread)
  const hasConversationId = item.item.conversationId != null

  // Check if the subject starts with "RE:" (case-insensitive)
  const subject = await item.getSubject()
  const subjectStartsWithRe = /^re:/i.test(subject)

  // Check if there's a parent message ID
  let parentMessageId
  try {
    const customProperties = await item.getCustomProperties()
    parentMessageId = customProperties.get('parentMessageId')
  } catch (e) {
    console.error('Error getting custom properties:', e)
    parentMessageId = null
  }

  // If any of these conditions are true, it's likely a reply
  return hasConversationId || subjectStartsWithRe || parentMessageId != null
}

export const melodyLoftAzure = async (state: LoftRecord, { gateway, office }: LoftServices) => {
  const recipients = await office.item?.getRecipients()
  const originalMessageId: string = await office.item.getInternetMessageId()

  let subject = (await state.getAsync('subject')) || ''
  const isReply = await isReplyMessage(office)
  if (isReply && !subject.toLowerCase().startsWith('re:')) {
    subject = `RE: ${subject}`
  }

  const me = await state.getAsync('me').catch(noop)

  return gateway.mailings.loftAzure({
    syncId: state.syncId,
    restItemId: state.restItemId,
    ...(await office.mailbox.restConnection(
      me?.data._private_fields.features.nested_app_auth_enabled
    )),
    templateId: (await state.getAsync('templateId')) || null,
    to: state.emailAddress || '',
    additionalTo: filterRecipients(recipients?.to || [], state.emailAddress),
    subject: (await state.getAsync('subject')) || '',
    platform: office.mailbox.platform,
    crmReferences: (await state.getAsync('crmReferences')) || {},
    inReplyTo: isReply ? originalMessageId : undefined,
    references: isReply ? originalMessageId : undefined,
  })
}

const generateSyncId = (): string =>
  // @ts-expect-error
  ([1e7] + -1e11).replace(/[018]/g, a => (a ^ ((Math.random() * 16) >> (a / 4))).toString(16))

export const setSyncMark = async (state: LoftRecord, { office }: LoftServices) => {
  if (office.mailbox.platform === PlatformType.OFFICE_ONLINE) {
    return state.set('syncId', '')
  }

  const syncId = generateSyncId()
  await office.item.prependBody(`<a href="#sync-${syncId}">&zwnj;</a>`)
  return state.set('syncId', syncId)
}

export const configureGateway = ({ session }: LoftRecord): void => {
  if (!session) throw new Error('API Urls are not set')
  melodyRequest.setBaseUrl(session.environment.melody_api_url)
  publicRequest.setBaseUrl(session.environment.public_api_url)
}

export const melodyMarkLofted = async ({ mailingId }: LoftRecord, { gateway }: LoftServices) => {
  if (mailingId) {
    return gateway.mailings.sentFromOutlook(mailingId).catch(noop)
  }
}

export const itemClose = (_: LoftRecord, { office }: LoftServices) => office.item.close()

export const appointmentClose = async (state: LoftRecord, { office }: LoftServices) => {
  const eventData = await state.getAsync('eventData').catch(noop)
  const me = await state.getAsync('me').catch(noop)

  if (
    isDesktopWindows() &&
    !me?.data._private_fields.features.outlook_windows_classic_onsend_lofting &&
    (!eventData || !eventData.event_id)
  ) {
    office.item.close()
  }
}

export const loftSuccess = async (state: LoftRecord, { gateway }: LoftServices) => {
  return gateway.stats
    .loftSuccess([
      `email_client_type:${await state.getEmailClientType()}`,
      `strategy:${state.strategy}`,
    ])
    .catch(noop)
}

export const ensureSyncIdSaved = async (state: LoftRecord, { office }: LoftServices) => {
  const body = await office.item.getBody()

  if (
    office.mailbox.platform !== PlatformType.OFFICE_ONLINE &&
    !body.includes(`sync-${state.syncId}`)
  ) {
    throw new Error('no sync tag in the body')
  }
}

const isAzure = (emailClientType: string) => emailClientType === 'azure'
const isAzureBackendLoftEnabled = (user?: Me) =>
  Boolean(user?.data._private_fields.features.outlook_backend_loft_azure)

export const chooseStrategy = async (state: LoftRecord, { office, gateway }: LoftServices) => {
  const itemType = await office.item.getItemType()
  if (itemType === 'appointment') {
    return state.set('strategy', CALENDAR_LOFTING)
  }

  if (
    isAzureBackendLoftEnabled(await state.getAsync('me')) &&
    isAzure(await state.getEmailClientType())
  ) {
    try {
      const azure = await gateway.user.azure()
      const emailMatch =
        azure.email_address.toLowerCase() === office.userProfile.emailAddress.toLowerCase()
      if (azure.configured && emailMatch) {
        return state.set('strategy', BACKEND_AZURE)
      }
    } catch (e) {
      return state.set('strategy', FRONTEND_EWS)
    }
  }

  return state.set('strategy', FRONTEND_EWS)
}

export const awaitAsync = (
  key: keyof LoftAsyncProps,
  func: (services: LoftServices) => Promise<unknown> | undefined
) => {
  const saver = async (state: LoftRecord, deps: LoftServices) =>
    state.setAsync(key, await func(deps))

  saver.operation = `awaitAsync: ${key}`
  return saver
}

export const withOperationName = (exportedFunctions: any) => {
  Object.keys(exportedFunctions).forEach(key => {
    if (typeof exportedFunctions[key] === 'function') {
      exportedFunctions[key].operation = key
    }
  })
}

export const startProgress = (_: LoftRecord, { office }: LoftServices) => {
  const loftMessages = getLoftMessages()
  office.notifications.removeAll()
  office.notifications.startProgress(notificationTypes.PROGRESS, loftMessages.preparing)
}

export const updateProgress = (key: keyof LoftMessages) => {
  const updater = (_: LoftRecord, { office }: LoftServices) => {
    return office.notifications.updateProgress(notificationTypes.PROGRESS, getLoftMessages()[key])
  }

  updater.operation = `updateProgress: ${key}`
  return updater
}

export const stopProgress = async (_: LoftRecord, { office }: LoftServices) => {
  const loftMessages = getLoftMessages()
  office.notifications.remove(notificationTypes.PROGRESS)
  const itemType = await office.item.getItemType()
  const appointmentLoftedMessage = isDesktopWindows()
    ? loftMessages.lofted
    : loftMessages.appointmentLofted
  office.notifications.addInfo(
    notificationTypes.INFO,
    itemType === 'appointment' ? appointmentLoftedMessage : loftMessages.lofted
  )
}

export const setUserLocale = async (state: LoftRecord) => {
  const me = await state.getAsync('me').catch(noop)
  const locale = me?.data._private_fields.locale
  if (locale) {
    await refreshLocaleData(locale)
  }
}

export const setTracingContext = async (state: LoftRecord) => {
  const user = await state.getAsync('me').catch(noop)

  if (!user) {
    return
  }

  setUser({
    id: String(user.data.id),
    email: user.data.email,
    username: user.data.name,
    user_guid: user.data.guid,
    team_id: user.data.team.id,
    permitted_localities: user.data._private_fields.permitted_localities,
  })

  setContext('user_info', {
    user_guid: user.data.guid,
    team_id: user.data.team.id,
    permitted_localities: user.data._private_fields.permitted_localities,
  })

  addEventProcessor(function(event) {
    if (!user.data._private_fields.permitted_localities.includes('us')) {
      return null
    }

    return event
  })
}
